<template>
  <!-- horizon rail (FI-1869): edge-attached frosted sidebar holding the FULL
       nav tree -- every major section as an expandable node with its pages
       indented beneath (v27 sidebars attach to the window edge; no floating
       island). tenant switcher at the top, collapse toggle at the foot; the
       account lives in the top bar (classic parity), and help lives BOTH
       here (round 13 section, incl. the fleet assistant launch row) and in
       the desktop top-bar menu. rows carry hover
       tooltips whose text comes from the registry desc lines. renders only
       under the horizon theme (gated in App.vue). -->
  <div>
    <div v-if="navState.mobileOpen" class="fi-sn-scrim" @click="closeMobileNav"></div>
    <nav
      id="fi-sn-drawer"
      class="fi-sn"
      :class="{ 'fi-sn--collapsed': railCollapsed, 'fi-sn--mobile-open': navState.mobileOpen }"
      aria-label="Main navigation"
    >
      <!-- tenant switcher rides the top of the rail (chaz round 9) -->
      <div v-if="!railCollapsed" class="fi-sn-tenant">
        <select
          v-if="tenantOptions.length > 1"
          v-model="user.tenant"
          class="fi-sn-tenant-select"
          aria-label="Tenant"
          @change="onTenantChange"
        >
          <option v-for="opt in tenantOptions" :key="optValue(opt)" :value="optValue(opt)">{{ optText(opt) }}</option>
        </select>
        <div v-else class="fi-sn-tenant-label">{{ currentTenantLabel }}</div>
        <button
          v-if="ctx.isEffectiveAdmin"
          class="fi-sn-icon-btn fi-sn-copy"
          :title="'Copy tenant id (' + user.tenant + ')'"
          @click="copyTenant"
        >
          <i class="fa fa-clipboard" aria-hidden="true"></i>
        </button>
      </div>
      <!-- expanded: the full section tree -->
      <div v-if="!railCollapsed" class="fi-sn-tree">
        <template v-for="(s, idx) in treeSections">
          <div v-if="idx === firstUtilityIndex" :key="s.key + '-divider'" class="fi-sn-divider"></div>
          <div :key="s.key" class="fi-sn-node">
            <!-- leaf section: the row itself navigates -->
            <router-link
              v-if="!hasChildren(s)"
              v-b-tooltip.hover.right="rowTip(s.desc)"
              :to="s.landing"
              class="fi-sn-sec fi-sn-sec--leaf"
              :class="{ 'fi-sn-sec--current': s.key === activeSectionKey }"
              :aria-current="s.key === activeSectionKey ? 'page' : null"
            >
              <i class="fa fi-sn-sec-icon" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-sec-label">{{ s.label }}</span>
            </router-link>
            <!-- branch section: the row toggles its subtree -->
            <button
              v-else
              v-b-tooltip.hover.right="rowTip(s.desc)"
              class="fi-sn-sec"
              :class="{ 'fi-sn-sec--current': s.key === activeSectionKey }"
              :aria-expanded="isExpanded(s.key) ? 'true' : 'false'"
              @click="onSectionToggle(s.key)"
            >
              <i class="fa fi-sn-sec-icon" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-sec-label">{{ s.label }}</span>
              <i class="fa fi-sn-caret" :class="isExpanded(s.key) ? 'fa-angle-down' : 'fa-angle-right'" aria-hidden="true"></i>
            </button>

            <div v-if="hasChildren(s) && isExpanded(s.key)" class="fi-sn-children">
              <router-link
                v-if="s.ownLanding && s.landing"
                v-b-tooltip.hover.right="rowTip(s.desc)"
                :to="s.landing"
                class="fi-sn-item"
                :class="{ 'fi-sn-item--active': isLandingActive(s) }"
                :aria-current="isLandingActive(s) ? 'page' : null"
              >Overview</router-link>
              <template v-for="group in s.groups">
                <div v-if="group.label && railItems(group).length" :key="s.key + '-' + group.key + '-label'" class="fi-sn-group-label">{{ group.label }}</div>
                <template v-for="item in railItems(group)">
                  <a
                    v-if="item.href"
                    :key="item.key"
                    v-b-tooltip.hover.right="rowTip(item.desc)"
                    :href="item.href"
                    target="_blank"
                    rel="noopener"
                    class="fi-sn-item"
                  >{{ item.label }} <i class="fa fa-external-link fi-sn-ext" aria-hidden="true"></i></a>
                  <!-- action rows (round 13: the help section's fleet
                       assistant) fire an app event instead of routing -->
                  <button
                    v-else-if="item.action"
                    :key="item.key"
                    v-b-tooltip.hover.right="rowTip(item.desc)"
                    type="button"
                    class="fi-sn-item"
                    :class="{ 'fi-sn-item--active': isActionActive(item) }"
                    @click="onItemAction(item)"
                  >{{ item.label }}</button>
                  <router-link
                    v-else
                    :key="item.key"
                    v-b-tooltip.hover.right="rowTip(item.desc)"
                    :to="item.to"
                    class="fi-sn-item"
                    :class="{ 'fi-sn-item--active': isItemActive(item) }"
                    :aria-current="isItemActive(item) ? 'page' : null"
                  >{{ item.label }}</router-link>
                </template>
              </template>
            </div>
          </div>
        </template>
      </div>

      <!-- collapsed: icon strip of every section -->
      <div v-else class="fi-sn-tree fi-sn-tree--icons">
        <template v-for="(s, idx) in treeSections">
          <div v-if="idx === firstUtilityIndex" :key="s.key + '-divider'" class="fi-sn-divider fi-sn-divider--icons"></div>
          <!-- a section can resolve without a landing (e.g. help when the
               help center is feature-disabled: only action/external rows
               remain); the strip has nowhere to route it, so it hides
               there and stays reachable from the expanded tree -->
          <router-link
            v-if="s.landing"
            :key="s.key"
            v-b-tooltip.hover.right="iconTip(s)"
            :to="s.landing"
            class="fi-sn-icon-btn"
            :class="{ 'fi-sn-icon-btn--active': s.key === activeSectionKey }"
            :aria-label="s.label"
            :aria-current="s.key === activeSectionKey ? 'page' : null"
          >
            <i class="fa" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
          </router-link>
        </template>
      </div>

      <!-- foot: the collapse toggle anchors bottom-left at the SAME offsets
           in both states (chaz round 9), so it never moves out from under
           the pointer when clicked. the mobile drawer has no collapse
           concept, so no foot there -->
      <div v-if="!navState.mobileOpen" class="fi-sn-foot">
        <button
          v-b-tooltip.hover.right="rowTip(navState.collapsed ? 'Expand navigation' : 'Collapse navigation')"
          class="fi-sn-icon-btn fi-sn-collapse-btn"
          :aria-label="navState.collapsed ? 'Expand navigation' : 'Collapse navigation'"
          @click="toggleCollapsed"
        >
          <!-- standard panel-left glyph: left column filled while the rail
               is open, outline only while collapsed -->
          <svg class="fi-sn-collapse-glyph" viewBox="0 0 20 16" aria-hidden="true">
            <rect x="1" y="1" width="18" height="14" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <line x1="7.5" y1="1.8" x2="7.5" y2="14.2" stroke="currentColor" stroke-width="1.6"/>
            <rect v-if="!navState.collapsed" x="2" y="2" width="4.6" height="12" rx="1.6" fill="currentColor" stroke="none"/>
          </svg>
        </button>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { buildNavContext, resolveNav, sectionKeyForRoute } from '@/nav/registry'
import { navState, setSectionExpanded, clearSectionExpanded, toggleCollapsed, setMobileOpen, noHoverDevice } from '@/nav/nav_state'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'

export default {
  name: 'fi_section_nav',
  data() {
    return {
      navState,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    ctx() {
      return buildNavContext(this.user, this.viewAs)
    },
    nav() {
      return resolveNav(this.ctx)
    },
    treeSections() {
      // top sections first, help/settings/admin after the divider --
      // resolveNav already returns them in that order. contextual "you are
      // here" rows (notifications / profile, reached from the top-bar
      // chrome) render only while their page is the active route.
      return this.nav.filter(s => s.surfacing !== 'contextual' || this.isContextualActive(s))
    },
    railCollapsed() {
      // the desktop rail can be collapsed to the icon strip; the mobile
      // drawer has no collapsed form -- it always presents the full tree
      // (collapsed defaults true under 1240px, so without this every phone
      // opened the drawer onto the icon strip with no way out)
      return this.navState.collapsed && !this.navState.mobileOpen
    },
    firstUtilityIndex() {
      const idx = this.treeSections.findIndex(s => s.surfacing === 'utility')
      return idx > 0 ? idx : -1
    },
    activeSectionKey() {
      const fromRegistry = sectionKeyForRoute(this.ctx, this.$route.name)
      if (fromRegistry) {
        const sec = this.nav.find(s => s.key === fromRegistry)
        // a contextual hit only counts while its row actually renders: the
        // profile landing is query-matched to the viewer, so an admin on
        // ANOTHER user's page falls through to the settings path match
        if (!sec || sec.surfacing !== 'contextual' || this.isContextualActive(sec)) return fromRegistry
      }
      const path = this.$route.path || ''
      if (path.startsWith('/reports')) return 'reports'
      if (path.startsWith('/maps')) return 'maps'
      if (path.startsWith('/bookings')) return 'bookings'
      if (path.startsWith('/admin-tools')) return 'admin'
      if (path.startsWith('/settings') || path.startsWith('/tenant_config') || path.startsWith('/user')) return 'settings'
      return null
    },
    viewAsNonAdmin() {
      return this.viewAs === 'non-admin'
    },
    tenantOptions() {
      // mirrors navbar visibleTenantOptions (navbar.vue:681-693): muggle
      // preview narrows to same-fleet tenants by id prefix
      const all = (this.user && this.user.tenant_options) || []
      if (!this.viewAsNonAdmin) return all
      const current = this.user && this.user.tenant
      if (!current || typeof current !== 'string') return all
      const prefix = current.split('-', 1)[0]
      if (!prefix) return all
      const sameFleet = all.filter(opt => {
        const v = typeof opt === 'object' ? opt.value : opt
        return typeof v === 'string' && v.split('-', 1)[0] === prefix
      })
      return sameFleet.length > 0 ? sameFleet : all
    },
    currentTenantLabel() {
      const hit = this.tenantOptions.find(opt => this.optValue(opt) === this.user.tenant)
      return hit ? this.optText(hit) : this.user.tenant
    },
  },
  watch: {
    // drilling into a page closes the mobile drawer; desktop collapse state
    // is intentionally NOT touched by navigation
    $route() {
      setMobileOpen(false)
    },
    // arriving in a section always reveals its pages: a stale user-collapse
    // override would defeat the active-section rule in isExpanded, so drop
    // it. leaving a section needs no handling here -- active-rule expansion
    // evaporates on its own, and an explicit user-open sticks by design.
    activeSectionKey: {
      immediate: true,
      handler(key) {
        if (!key) return
        if (this.navState.expanded[key] === false) clearSectionExpanded(key)
      },
    },
  },
  methods: {
    toggleCollapsed,
    // tri-state expansion (nav_state.expanded): explicit true/false overrides
    // win; with no override, only the active section renders open
    isExpanded(key) {
      const v = this.navState.expanded[key]
      if (v == null) return key === this.activeSectionKey
      return !!v
    },
    // the toggle flips what the user SEES, not the raw stored bit -- closing
    // an active-rule-opened section must store an explicit false
    onSectionToggle(key) {
      setSectionExpanded(key, !this.isExpanded(key))
    },
    // hover tooltips (bootstrap-vue): desc lines come from the registry,
    // which copies them from the overview/dashboard pages. an empty title
    // renders no tooltip, so rows without a desc opt out naturally.
    // suppressed on touch devices: a tap emulates hover and the tip sticks
    // open (branch rows keep the drawer open under it)
    rowTip(text) {
      if (noHoverDevice()) return { title: '' }
      return { title: text || '', delay: { show: 450, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    iconTip(s) {
      if (noHoverDevice()) return { title: '' }
      // the collapsed strip hides labels, so the tooltip carries label + desc
      const title = s.desc
        ? '<div class="fi-sn-tip-label">' + s.label + '</div><div>' + s.desc + '</div>'
        : s.label
      return { title, html: !!s.desc, delay: { show: 300, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    hasChildren(s) {
      return !s.landingOnly && s.groups.some(g => this.railItems(g).length > 0)
    },
    // contextual visibility matches the landing by route NAME (the help
    // center's ?tab= disambiguators must not hide the row) plus any query
    // keys the landing itself pins (the profile row pins ?username=<own>)
    isContextualActive(s) {
      if (!s.landing || this.$route.name !== s.landing.name) return false
      const lq = s.landing.query || {}
      const rq = this.$route.query || {}
      for (const k of Object.keys(lq)) {
        if (String(rq[k]) !== String(lq[k])) return false
      }
      return true
    },
    railItems(group) {
      return group.items.filter(i => i.placement === 'rail')
    },
    isLandingActive(s) {
      // same query-aware match as items: the settings landing carries
      // ?tab=tab_overview, so it must not light up on every settings tab
      return !!(s.landing && s.landing.name && this.isItemActive({ to: s.landing }))
    },
    isItemActive(item) {
      if (!item.to || this.$route.name !== item.to.name) return false
      // every key the item's query specifies must match the route...
      const iq = item.to.query || {}
      const rq = this.$route.query || {}
      for (const k of Object.keys(iq)) {
        if (String(rq[k]) !== String(iq[k])) return false
      }
      // loose rows own their whole multi-tab page (the help center row
      // stays lit on ?tab= deep links)
      if (item.loose) return true
      // ...and disambiguator keys present on the route must not be silently
      // ignored (else "Help Articles" also lights up on ?section=faq)
      for (const k of ['source', 'section', 'tab']) {
        if (rq[k] != null && iq[k] == null) return false
      }
      return true
    },
    // action items fire an app event instead of routing (the help section's
    // fleet assistant row opens the panel via the launcher's $root listener).
    // closing the drawer mirrors what the $route watcher does for links.
    onItemAction(item) {
      setMobileOpen(false)
      this.$root.$emit(item.action)
    },
    // the assistant row reads active while the full-page assistant is the
    // route, so the tree still says "you are here" for an action row
    isActionActive(item) {
      return item.action === 'open-fleet-assistant' && this.$route.name === 'fleet_assistant'
    },
    optValue(opt) {
      return typeof opt === 'object' ? opt.value : opt
    },
    optText(opt) {
      return typeof opt === 'object' ? opt.text : opt
    },
    onTenantChange() {
      localStorage.setItem('selected_tenant', this.user.tenant)
      // mirror navbar set_tenant_id (navbar.vue:484-499): keep an in-route
      // ?tenant= query honest and drop cross-tenant detail ids
      const q = this.$route.query
      if (q && q.tenant && q.tenant !== this.user.tenant) {
        const next = { ...q, tenant: this.user.tenant }
        delete next.event_id
        this.$router.replace({ query: next }).catch(() => {})
      }
    },
    copyTenant() {
      copyToClipboard(String(this.user.tenant))
    },
    closeMobileNav() {
      setMobileOpen(false)
    },
  },
}
</script>

<style scoped>
.fi-sn {
  /* fixed, not sticky: the component's wrapper div is exactly as tall as the
     rail, so sticky has no travel and scrolls away with the page */
  position: fixed;
  top: 52px;
  left: 0;
  bottom: 0;
  z-index: 1020;
  display: flex;
  flex-direction: column;
  width: 264px;
  /* App.vue's .container-fluid centers text app-wide; a tree is left-aligned */
  text-align: left;
  background: var(--fi-glass-bg);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border-right: 1px solid var(--fi-glass-line);
  transition: width var(--fi-motion-quick);
}
.fi-sn--collapsed { width: 56px; }

.fi-sn-tenant-select {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px; /* ios safari zooms inputs under 16px */
  color: var(--fi-chrome-ink-strong);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--fi-radius-sm);
  padding: 4px 2px;
}
.fi-sn-tenant-select:hover { border-color: var(--fi-glass-line); }
.fi-sn-tenant-label {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-chrome-ink-strong);
  padding: 6px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fi-sn-copy { opacity: 0.55; }
.fi-sn-copy:hover { opacity: 1; }

.fi-sn-tree {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 8px 8px 6px;
  /* slim quiet scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--fi-chrome-scrollbar) transparent;
}
.fi-sn-tree::-webkit-scrollbar { width: 8px; }
.fi-sn-tree::-webkit-scrollbar-track { background: transparent; }
.fi-sn-tree::-webkit-scrollbar-thumb {
  background: var(--fi-chrome-scrollbar);
  border-radius: 4px;
  /* transparent border + padding-box clip = a 4px thumb with breathing room */
  border: 2px solid transparent;
  background-clip: padding-box;
}
/* glyph ink sized to read like the 16px fa glyphs on sibling icon buttons */
.fi-sn-collapse-glyph {
  display: block;
  width: 17.5px;
  height: 14px;
}
.fi-sn-tree--icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
}
.fi-sn-node { margin-bottom: 1px; }
.fi-sn-divider {
  height: 1px;
  margin: 8px 10px;
  background: var(--fi-glass-line);
}
.fi-sn-divider--icons { width: 24px; margin: 6px 0; }

/* section rows: icon + label, left-aligned; branches add a trailing caret */
.fi-sn-sec {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 7px 8px 7px 10px;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-ink) !important;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
.fi-sn-sec:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-sn-sec-icon {
  flex: 0 0 auto;
  width: 18px;
  text-align: center;
  font-size: 14px;
  color: var(--fi-chrome-muted);
}
.fi-sn-sec-label {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* v27 cue: the active section keeps a colored icon so the tree reads at a
   glance; the page-level highlight lives on the item rows */
.fi-sn-sec--current { color: var(--fi-chrome-ink-strong) !important; }
.fi-sn-sec--current .fi-sn-sec-icon { color: var(--fi-chrome-accent); }
/* leaf sections ARE the page, so the row carries the item-style tint */
.fi-sn-sec--leaf.fi-sn-sec--current,
.fi-sn-sec--leaf.fi-sn-sec--current:hover {
  background: var(--fi-chrome-active-bg);
  color: var(--fi-chrome-accent) !important;
}
.fi-sn-caret {
  flex: 0 0 auto;
  font-size: 13px;
  color: var(--fi-chrome-muted);
}

/* children: indented under the section icon column */
.fi-sn-children {
  margin: 1px 0 5px;
  padding-left: 24px;
}
.fi-sn-group-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fi-chrome-eyebrow);
  padding: 8px 8px 3px 13px;
}
/* item rows carry the classic shell-rail active language: steelblue left
   rail + pale blue tint + steelblue text (.ms-nav-item.active et al.) */
.fi-sn-item {
  display: block;
  padding: 5px 8px 5px 13px;
  border-left: 3px solid transparent;
  border-radius: 0 var(--fi-radius-sm) var(--fi-radius-sm) 0;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-text) !important;
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color var(--fi-motion-quick), border-color var(--fi-motion-quick);
}
.fi-sn-item:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-sn-item--active,
.fi-sn-item--active:hover {
  background: var(--fi-chrome-active-bg);
  border-left-color: var(--fi-chrome-accent);
  color: var(--fi-chrome-accent) !important;
  font-weight: 600;
}
.fi-sn-ext { font-size: 10px; opacity: 0.6; }
/* action rows are buttons wearing the item look; strip the native chrome
   (no `font` shorthand -- reboot already inherits family/size on buttons) */
button.fi-sn-item {
  width: 100%;
  border-top: none;
  border-right: none;
  border-bottom: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.fi-sn-tenant {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px;
  border-bottom: 1px solid var(--fi-glass-line);
}
/* foot bar: 11px inset = (56px rail - 34px button) / 2, so the left-anchored
   toggle centers on the collapsed icon column AND holds the exact same spot
   when the rail is expanded */
.fi-sn-foot {
  flex: 0 0 auto;
  padding: 8px 11px;
  border-top: 1px solid var(--fi-glass-line);
}
.fi-sn-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-ink) !important;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;
}
.fi-sn-icon-btn:hover { background: var(--fi-chrome-hover-bg-strong); color: var(--fi-chrome-ink-strong) !important; }
.fi-sn-icon-btn--active,
.fi-sn-icon-btn--active:hover { background: var(--fi-chrome-active-bg-strong); color: var(--fi-chrome-accent) !important; }
/* bootstrap's reboot draws a focus ring on plain :focus, which lingers after
   a mouse click (section rows kept it after toggling); keyboard focus only */
.fi-sn-sec:focus,
.fi-sn-item:focus,
.fi-sn-icon-btn:focus {
  outline: none;
  box-shadow: none;
}
.fi-sn-sec:focus-visible,
.fi-sn-item:focus-visible,
.fi-sn-icon-btn:focus-visible,
.fi-sn-tenant-select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--fi-action-primary-ring);
}
.fi-sn-scrim {
  display: none;
}
@media (max-width: 991px) {
  .fi-sn { display: none; }
  .fi-sn--mobile-open {
    display: flex;
    position: fixed;
    /* sit below the persistent 52px top bar so its top-left toggle is never
       overlapped -- the same fixed-spot button opens and closes the drawer */
    top: 52px;
    left: 0;
    bottom: 0;
    width: 300px;
    height: auto;
    z-index: 2000;
  }
  .fi-sn-scrim {
    display: block;
    position: fixed;
    /* clear the top bar so the toggle stays live (not covered or dimmed) */
    inset: 52px 0 0 0;
    background: var(--fi-chrome-scrim);
    z-index: 1999;
  }
  /* phone touch targets: ~42px section rows, ~38px item rows (44 is the
     hig ideal; the tree is dense, these are the compromise heights) */
  .fi-sn-sec { padding-top: 11px; padding-bottom: 11px; }
  .fi-sn-item { padding-top: 9px; padding-bottom: 9px; }
  /* last rows clear the home indicator */
  .fi-sn-tree { padding-bottom: calc(8px + env(safe-area-inset-bottom)); }
}
</style>

<style>
/* bv tooltips mount at body level, so their styling can't be scoped */
.fi-sn-tip .tooltip-inner {
  max-width: 280px;
  text-align: left;
}
.fi-sn-tip .fi-sn-tip-label {
  font-weight: 700;
  margin-bottom: 2px;
}
</style>
